// ./src/utils/api/TemplateCategoryService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const TEMPLATE_CATEGORY_LIST_QUERY = gql`
  query TemplateCategoryList(
    $filter: TemplateCategoryFilter
    $params: PaginationAndSorting
  ) {
    templateCategoryList(filter: $filter, params: $params) {
      code
      data {
        id
        name
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TEMPLATE_CATEGORY_GET_QUERY = gql`
  query TemplateCategoryGet($id: ID) {
    templateCategoryGet(id: $id) {
      code
      data {
        id
        name
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TEMPLATE_CATEGORY_GET_MANY_QUERY = gql`
  query TemplateCategoryGetMany($ids: [ID]) {
    templateCategoryGetMany(ids: $ids) {
      code
      data {
        id
        name
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

export interface TemplateCategory {
  id?: string | undefined;
  name?: string | undefined;
}

export interface TemplateCategoryFilter {
  id?: string[] | null | undefined;
  name?: string | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface TemplateCategoryWhereInput {
  id?: string | undefined;
}

/**
 * Template Category service that handles all template category-related API calls
 * Read-only operations: list, get, getMany
 */
export class TemplateCategoryService {
  /**
   * Get a list of template categories with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: TemplateCategoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(TEMPLATE_CATEGORY_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single template category by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(TEMPLATE_CATEGORY_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple template categories by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      TEMPLATE_CATEGORY_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }
}
